/**
 * The one integration point between an action and progress reporting (plan D6).
 *
 * Defined in Phase 04 although nothing here creates a job yet, precisely so
 * Phase 05 does not invent its own. An action dialog calls `track(ref)` on a
 * successful submission and is finished; the drawer owns polling, presentation
 * and the re-check control from then on. If each screen wired its own progress
 * UI, `EXPIRED` would be rendered correctly in some of them.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode, type ReactElement } from 'react';

export interface JobDrawerApi {
  /** Job refs being watched, newest first. */
  tracked: readonly string[];
  isOpen: boolean;
  track(ref: string): void;
  untrack(ref: string): void;
  open(): void;
  close(): void;
}

const JobDrawerContext = createContext<JobDrawerApi | null>(null);

/** Enough to see a session's work; beyond this the jobs list is the answer. */
const MAX_TRACKED = 20;

export function useJobDrawer(): JobDrawerApi {
  const ctx = useContext(JobDrawerContext);
  if (!ctx) throw new Error('useJobDrawer must be used inside <JobDrawerProvider>');
  return ctx;
}

export function JobDrawerProvider({ children }: { children: ReactNode }): ReactElement {
  const [tracked, setTracked] = useState<readonly string[]>([]);
  const [isOpen, setOpen] = useState(false);

  const track = useCallback((ref: string) => {
    setTracked((current) =>
      current.includes(ref) ? current : [ref, ...current].slice(0, MAX_TRACKED),
    );
    // Opening on track is deliberate: the user just asked for something that
    // takes time, and silent background work is how people press a button
    // twice.
    setOpen(true);
  }, []);

  const untrack = useCallback((ref: string) => {
    setTracked((current) => current.filter((r) => r !== ref));
  }, []);

  const value = useMemo<JobDrawerApi>(
    () => ({
      tracked,
      isOpen,
      track,
      untrack,
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [tracked, isOpen, track, untrack],
  );

  return <JobDrawerContext.Provider value={value}>{children}</JobDrawerContext.Provider>;
}
